import { Fragment, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { Button, Checkbox, Input, Textarea } from '@mlm/ui';
import { rankIconUrl, uploadRankIcon } from '../../lib/api';

/** Requirement keys the member rank tracker can hide per rank. */
export type RankRequirementKey = 'ppv' | 'gpv' | 'legs' | 'directs' | 'total_earned';

const TRACKER_KEYS: { key: RankRequirementKey; label: string }[] = [
  { key: 'ppv', label: 'PPV' },
  { key: 'gpv', label: 'GPV' },
  { key: 'legs', label: 'Legs' },
  { key: 'directs', label: 'Directs' },
  { key: 'total_earned', label: 'Total Earned' },
];

export type StairstepRank = {
  code: string;
  min_ppv: number;
  min_gpv: number;
  min_legs: number;
  /** Min directly-sponsored active slots required for this rank (Phase C). */
  min_directs?: number;
  min_leg_gpv: number | null;
  min_leg_rank: string | null;
  payout_pct: number;
  discount_pct: number;
  /**
   * Same-rank royalty bonus %. When > 0, an equal-rank downline that breaks away
   * pays this rank a royalty on the leg's whole group PV (instead of 0). Set it
   * only on the specific ranks that should earn royalty; blank/0 = off.
   */
  royalty_pct?: number;
  /** Infinity Bonus override % for this rank, read by the infinity_bonus plan (Phase C). */
  percentage_override?: number;
  /** One-time rank-up bonus (paid when the tenant flag stairstep_rankup_bonus is on). */
  reward_cash?: number;
  reward_gc?: number;
  /** Min cumulative cash Total Earned of the slot across all plans (0 = none). */
  min_total_earned?: number;
  /** Display-only fields (never read by the comp math). */
  name?: string;
  description?: string;
  /** R2 key from `uploadRankIcon`, served publicly at `/ranks/<code>/icon`. */
  icon_key?: string;
  /** Requirements hidden from the member rank tracker. */
  tracker_hidden?: RankRequirementKey[];
};

export type StairstepConfig = {
  ranks: StairstepRank[];
};

export function StairstepEditor({
  value,
  onChange,
  readOnly,
}: {
  value: StairstepConfig;
  onChange: (next: StairstepConfig) => void;
  readOnly?: boolean;
}) {
  // Index of the rank whose Details row is expanded (follows the rank on reorder).
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  function setRank(i: number, patch: Partial<StairstepRank>) {
    const next = value.ranks.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    onChange({ ranks: next });
  }
  function removeRank(i: number) {
    onChange({ ranks: value.ranks.filter((_, idx) => idx !== i) });
    setOpenIdx((o) => (o === null || o === i ? null : o > i ? o - 1 : o));
  }
  /** Swap rank i with its neighbour (dir -1 = up = lower rank, +1 = down = higher). */
  function moveRank(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= value.ranks.length) return;
    const next = value.ranks.slice();
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange({ ranks: next });
    setOpenIdx((o) => (o === i ? j : o === j ? i : o));
  }
  function addRank() {
    onChange({
      ranks: [
        ...value.ranks,
        {
          code: `rank_${value.ranks.length + 1}`,
          min_ppv: 0,
          min_gpv: 0,
          min_legs: 0,
          min_directs: 0,
          min_total_earned: 0,
          min_leg_gpv: null,
          min_leg_rank: null,
          payout_pct: 0,
          discount_pct: 0,
          royalty_pct: 0,
          percentage_override: 0,
          reward_cash: 0,
          reward_gc: 0,
        },
      ],
    });
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium">Ranks</h3>
          <p className="text-[10px] text-muted-foreground">
            Hierarchy = list order, lowest → highest:{' '}
            <strong>top row is the lowest rank, bottom row the highest</strong> (use the ↑/↓ arrows
            to reorder). A slot earns the highest rank whose PPV / GPV / leg thresholds it meets.
            Hover a column header for what it does.
          </p>
        </div>
        {!readOnly ? (
          <Button size="sm" variant="outline" onClick={addRank}>
            <Plus className="h-3 w-3 mr-1" />
            Add rank
          </Button>
        ) : null}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border rounded-md min-w-[900px]">
          <thead className="text-left text-muted-foreground bg-muted/40 text-xs">
            <tr>
              <th
                className="px-3 py-2 font-medium"
                title="Rank order, lowest → highest. Use the arrows to reorder."
              >
                Rank #
              </th>
              <th className="px-3 py-2 font-medium">Code</th>
              <th
                className="px-3 py-2 font-medium"
                title="Minimum personal point value (own orders) for this rank."
              >
                Min PPV
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="Minimum group point value (own + all descendants) for this rank."
              >
                Min GPV
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="Minimum number of qualifying direct legs required for this rank."
              >
                Min legs
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="Minimum directly-sponsored ACTIVE slots required, in addition to qualifying legs."
              >
                Min directs
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="Cumulative Total Earned of the slot across all comp plans (cash). 0 = no requirement."
              >
                Min Total Earned
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="A leg counts toward Min legs if its group PV is at least this (when Min leg rank is blank)."
              >
                Min leg GPV
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="A leg counts toward Min legs only if its OWN rank is at least this rank code."
              >
                Min leg rank
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="This rank's differential commission %. An upline earns (uplinePct − downlinePct) on a downline's PPV."
              >
                Payout %
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="Same-rank royalty %. When set, an equal-rank downline that breaks away still pays this rank a royalty on the leg's whole group PV (instead of 0). Leave 0 to disable."
              >
                Royalty %
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="Infinity Bonus override % — read by the separate Infinity Bonus plan, NOT by Stairstep itself."
              >
                Infinity %
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="One-time cash bonus paid when a slot first reaches this rank (only when the tenant's rank-up bonus setting is on)."
              >
                Reward cash
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="One-time gift-cert bonus paid when a slot first reaches this rank (only when the tenant's rank-up bonus setting is on)."
              >
                Reward GC
              </th>
              <th className="px-3 py-2 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {value.ranks.length === 0 ? (
              <tr>
                <td colSpan={COLS} className="px-3 py-4 text-xs text-muted-foreground text-center">
                  No ranks configured. Click "Add rank" to start.
                </td>
              </tr>
            ) : (
              value.ranks.map((r, i) => (
                <Fragment key={i}>
                  <tr className="border-t">
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-1">
                        <span className="w-5 text-xs tabular-nums text-muted-foreground">
                          {i + 1}
                        </span>
                        {!readOnly ? (
                          <div className="flex flex-col">
                            <button
                              type="button"
                              onClick={() => moveRank(i, -1)}
                              disabled={i === 0}
                              className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                              aria-label="Move rank up (lower)"
                            >
                              <ChevronUp className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveRank(i, 1)}
                              disabled={i === value.ranks.length - 1}
                              className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                              aria-label="Move rank down (higher)"
                            >
                              <ChevronDown className="h-3 w-3" />
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-1.5">
                      <input
                        type="text"
                        className="h-8 w-28 px-2 rounded-md border bg-background text-xs font-mono"
                        value={r.code}
                        disabled={readOnly}
                        onChange={(e) => setRank(i, { code: e.target.value })}
                      />
                    </td>
                    {(
                      ['min_ppv', 'min_gpv', 'min_legs', 'min_directs', 'min_total_earned'] as const
                    ).map((key) => (
                      <td className="px-3 py-1.5" key={key}>
                        <input
                          type="number"
                          className="h-8 w-24 px-2 rounded-md border bg-background text-xs"
                          value={r[key] ?? 0}
                          disabled={readOnly}
                          step={key === 'min_legs' || key === 'min_directs' ? 1 : 0.01}
                          min={0}
                          onChange={(e) =>
                            setRank(i, {
                              [key]: Number(e.target.value) || 0,
                            } as Partial<StairstepRank>)
                          }
                        />
                      </td>
                    ))}
                    <td className="px-3 py-1.5">
                      <input
                        type="number"
                        className="h-8 w-24 px-2 rounded-md border bg-background text-xs"
                        placeholder=""
                        value={r.min_leg_gpv ?? ''}
                        disabled={readOnly}
                        step={0.01}
                        min={0}
                        onChange={(e) =>
                          setRank(i, {
                            min_leg_gpv: e.target.value === '' ? null : Number(e.target.value),
                          })
                        }
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      {/* Must be an actual rank CODE — a free-text typo (e.g. a
                        rank number) silently disables the leg-rank gate, so
                        constrain to configured codes. */}
                      <select
                        className="h-8 w-28 px-2 rounded-md border bg-background text-xs font-mono"
                        value={r.min_leg_rank ?? ''}
                        disabled={readOnly}
                        onChange={(e) =>
                          setRank(i, {
                            min_leg_rank: e.target.value === '' ? null : e.target.value,
                          })
                        }
                      >
                        <option value="">none</option>
                        {value.ranks
                          .filter((rk) => rk.code !== r.code)
                          .map((rk) => (
                            <option key={rk.code} value={rk.code}>
                              {rk.code}
                            </option>
                          ))}
                      </select>
                    </td>
                    {(['payout_pct', 'royalty_pct', 'percentage_override'] as const).map((key) => (
                      <td className="px-3 py-1.5" key={key}>
                        <input
                          type="number"
                          className="h-8 w-20 px-2 rounded-md border bg-background text-xs"
                          value={r[key] ?? 0}
                          disabled={readOnly}
                          step={0.01}
                          min={0}
                          max={100}
                          onChange={(e) =>
                            setRank(i, {
                              [key]: Number(e.target.value) || 0,
                            } as Partial<StairstepRank>)
                          }
                        />
                      </td>
                    ))}
                    {(['reward_cash', 'reward_gc'] as const).map((key) => (
                      <td className="px-3 py-1.5" key={key}>
                        <input
                          type="number"
                          className="h-8 w-24 px-2 rounded-md border bg-background text-xs"
                          value={r[key] ?? 0}
                          disabled={readOnly}
                          step={0.01}
                          min={0}
                          onChange={(e) =>
                            setRank(i, {
                              [key]: Number(e.target.value) || 0,
                            } as Partial<StairstepRank>)
                          }
                        />
                      </td>
                    ))}
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setOpenIdx(openIdx === i ? null : i)}
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          aria-expanded={openIdx === i}
                          aria-label={`Details for rank ${r.code}`}
                          title="Details: display name, description, icon, tracker visibility"
                        >
                          {openIdx === i ? (
                            <ChevronDown className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5" />
                          )}
                        </button>
                        {!readOnly ? (
                          <button
                            type="button"
                            onClick={() => removeRank(i)}
                            className="text-muted-foreground hover:text-destructive transition-colors"
                            aria-label={`Remove rank ${r.code}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                  {openIdx === i ? (
                    <tr className="bg-muted/20">
                      <td colSpan={COLS} className="px-3 py-3">
                        <RankDetails
                          rank={r}
                          readOnly={readOnly}
                          onPatch={(patch) => setRank(i, patch)}
                        />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Table column count (the Details row spans all of them). */
const COLS = 15;

/**
 * Expanded per-rank details: display name, description, icon and which
 * requirements the member tracker shows. Saved with the config on publish.
 */
function RankDetails({
  rank,
  readOnly,
  onPatch,
}: {
  rank: StairstepRank;
  readOnly?: boolean;
  onPatch: (patch: Partial<StairstepRank>) => void;
}) {
  // Object URL of an icon uploaded this session (the public route only serves
  // a PUBLISHED key, so an unsaved upload can't be previewed through it).
  const [local, setLocal] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => {
    return () => {
      if (local) URL.revokeObjectURL(local);
    };
  }, [local]);

  const hidden = new Set(rank.tracker_hidden ?? []);
  const src = local ?? (rank.icon_key ? rankIconUrl(rank.code) : null);

  async function pick(file: File) {
    setUploading(true);
    try {
      const { key } = await uploadRankIcon(file);
      setLocal(URL.createObjectURL(file));
      setImgFailed(false);
      onPatch({ icon_key: key });
    } finally {
      setUploading(false);
    }
  }

  function toggleTracker(key: RankRequirementKey, shown: boolean) {
    const next = new Set(hidden);
    if (shown) next.delete(key);
    else next.add(key);
    onPatch({ tracker_hidden: TRACKER_KEYS.map((t) => t.key).filter((k) => next.has(k)) });
  }

  return (
    <div className="grid max-w-3xl gap-4 sm:grid-cols-2">
      <label className="space-y-1">
        <span className="text-xs font-medium">Display name</span>
        <Input
          className="h-8 text-xs"
          placeholder={rank.code}
          value={rank.name ?? ''}
          disabled={readOnly}
          onChange={(e) => onPatch({ name: e.target.value || undefined })}
        />
        <span className="block text-[11px] text-muted-foreground">
          Shown to members and on the storefront. Blank = the rank code.
        </span>
      </label>
      <div className="space-y-1">
        <span className="text-xs font-medium">Icon</span>
        <div className="flex flex-wrap items-center gap-3">
          {src && !imgFailed ? (
            <img
              src={src}
              alt=""
              className="h-12 w-12 rounded border bg-white object-contain p-1"
              onError={() => setImgFailed(true)}
            />
          ) : rank.icon_key ? (
            <span
              className="max-w-[12rem] truncate rounded border bg-muted px-2 py-1 font-mono text-[10px] text-muted-foreground"
              title={rank.icon_key}
            >
              {rank.icon_key}
            </span>
          ) : (
            <div className="h-12 w-12 rounded border bg-muted" />
          )}
          {!readOnly && (
            <input
              type="file"
              accept="image/png,image/jpeg"
              className="max-w-full text-xs"
              disabled={uploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void pick(f);
                e.target.value = '';
              }}
            />
          )}
          {uploading && <span className="text-xs text-muted-foreground">Uploading…</span>}
          {rank.icon_key && !uploading && !readOnly && (
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-destructive"
              onClick={() => {
                setLocal(null);
                setImgFailed(false);
                onPatch({ icon_key: undefined });
              }}
            >
              Remove
            </button>
          )}
        </div>
        <span className="block text-[11px] text-muted-foreground">
          PNG or JPEG. Saved when you publish the config.
        </span>
      </div>
      <label className="space-y-1 sm:col-span-2">
        <span className="text-xs font-medium">Description</span>
        <Textarea
          rows={3}
          className="text-xs"
          value={rank.description ?? ''}
          disabled={readOnly}
          onChange={(e) => onPatch({ description: e.target.value || undefined })}
        />
      </label>
      <div className="space-y-1 sm:col-span-2">
        <span className="text-xs font-medium">Show on member tracker</span>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {TRACKER_KEYS.map((t) => (
            <label key={t.key} className="flex items-center gap-1.5 text-xs">
              <Checkbox
                checked={!hidden.has(t.key)}
                disabled={readOnly}
                onCheckedChange={(v) => toggleTracker(t.key, !!v)}
              />
              {t.label}
            </label>
          ))}
        </div>
        <span className="block text-[11px] text-muted-foreground">
          Requirements set to 0 are always hidden.
        </span>
      </div>
    </div>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- the empty-config factory stays beside its editor, like every sibling plan editor
export function emptyStairstepConfig(): StairstepConfig {
  return { ranks: [] };
}
